export function stripHtml(html: string) {
  return html.replace(/<[^>]*>?/gm, "");
}

/**
 * Decodes common HTML entities to their character equivalents
 */
export function decodeHtmlEntities(html: string): string {
  return html.replace(/&(#[xX]?[0-9a-fA-F]+|[a-zA-Z]+);/g, (match, entity) => {
    if (typeof entity !== "string") {
      return match;
    }
    if (entity.startsWith("#x") || entity.startsWith("#X")) {
      const codePoint = Number.parseInt(entity.slice(2), 16);
      return decodeCodePoint(codePoint, match);
    }
    if (entity.startsWith("#")) {
      const codePoint = Number.parseInt(entity.slice(1), 10);
      return decodeCodePoint(codePoint, match);
    }
    return NAMED_HTML_ENTITIES[entity] ?? match;
  });
}

const NAMED_HTML_ENTITIES: Record<string, string> = {
  amp: "&",
  gt: ">",
  lt: "<",
  nbsp: " ",
  quot: '"',
  apos: "'",
};

function decodeCodePoint(codePoint: number, fallback: string): string {
  if (!Number.isInteger(codePoint) || codePoint < 0 || codePoint > 0x10_ff_ff) {
    return fallback;
  }
  return String.fromCodePoint(codePoint);
}

/**
 * Convert HTML to readable plain text while PRESERVING block structure: decodes
 * entities, drops `<script>`/`<style>`, `<br>` → newline, `<li>` → `"• "`, block
 * elements (`p`, `div`, `h1`-`h6`, `blockquote`, `ul`/`ol`, table rows, …) → line
 * breaks; remaining tags are stripped, per-line whitespace is normalized,
 * consecutive blank lines are capped, the stray space before sentence
 * punctuation is dropped, and consecutive list bullets are kept tight.
 *
 * Use this when the output is shown to a human (e.g. dispute-evidence shop-policy
 * text). For single-line output use {@link extractTextFromHtml}; for a naive
 * tag-strip use {@link stripHtml}.
 *
 * NOTE: a model-tuned variant (`semanticallyStripHtmlTags`) lives in
 * `support-db-util`'s `faq-content-extraction.ts`, and the email body parser keeps
 * its own `bestEffortHtmlToText`; both are intentionally separate for now —
 * converging them onto this canonical helper is a follow-up.
 */
export function htmlToReadableText(html: string): string {
  return (
    decodeHtmlEntities(
      // Drop <script>/<style> blocks entirely — their contents aren't readable.
      html.replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, " "),
    )
      .replace(/<li[^>]*>/gi, "\n• ")
      // Line breaks: <br> and table rows. (</li> falls through to the strip below
      // — the <li> opener already added its line break.)
      .replace(/<br\s*\/?>|<\/tr>/gi, "\n")
      // Block elements → blank line between paragraphs/sections.
      .replace(/<\/(p|div|h[1-6]|ul|ol|blockquote|table)>/gi, "\n\n")
      // Strip every remaining tag (opening tags, table cells, spans, …).
      .replace(/<[^>]+>/g, " ")
      // Collapse spaces/tabs but keep newlines.
      .replace(/[^\S\n]+/g, " ")
      // Drop the space left before sentence punctuation when an inline tag was
      // stripped (e.g. "<strong>Exchanges</strong>:" → "Exchanges:").
      .replace(/ +([,.;:!?])/g, "$1")
      // Trim spaces around line breaks, then cap blank lines.
      .replace(/ *\n */g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      // Keep consecutive bullets tight (one per line); the blank line before a
      // list (after its lead-in paragraph) is preserved.
      .replace(/(• [^\n]*)\n\n(?=• )/g, "$1\n")
      .trim()
  );
}

export function extractTextFromHtml(html: string): string {
  return html
    .replace(/<[^>]*>?/gm, (match, offset) => {
      const beforeChar = offset > 0 ? html[offset - 1] : "";
      const afterChar =
        offset + match.length < html.length ? html[offset + match.length] : "";
      if (
        beforeChar &&
        afterChar &&
        !/\s/.test(beforeChar) &&
        !/\s/.test(afterChar)
      ) {
        return " ";
      }
      return "";
    })
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Replaces occurrences of &quot; that are not
 * between angle brackets with the character `"`.
 *
 * Quotation marks inside attribute values need to be escaped to
 * avoid ending an attribute early, but quotation marks that aren't
 * inside attribute values don't need to be escaped for any reason.
 */
export function unescapeNonAttributeQuotes(html: string): string {
  let ret = "";
  let insideAngleBrackets = false;
  /** " or ' if we are in a string, null otherwise */
  let delimiterForCurrentString: "'" | '"' | null = null;
  let i = 0;
  const toReplace = "&quot;";
  function setInsideAngleBrackets(newVal: boolean) {
    if (insideAngleBrackets && newVal) {
      throw new Error(
        `HTML is malformed: Found < inside tag at index ${i}. html: ${html}`,
      );
    }
    insideAngleBrackets = newVal;
  }

  while (i < html.length - toReplace.length) {
    if (
      !insideAngleBrackets &&
      html.slice(i, i + toReplace.length) === toReplace
    ) {
      ret += '"';
      i += toReplace.length;
      continue;
    }
    const c = html[i];

    // Quotation marks outside angle brackets don't delimit strings
    if (insideAngleBrackets && (c === '"' || c === "'")) {
      if (delimiterForCurrentString === c) {
        delimiterForCurrentString = null;
      } else {
        delimiterForCurrentString = c;
      }
    }

    // Angle brackets inside strings don't count
    if (delimiterForCurrentString === null) {
      if (c === "<") {
        setInsideAngleBrackets(true);
      } else if (c === ">") {
        setInsideAngleBrackets(false);
      }
    }
    ret += c;
    i++;
  }
  ret += html.substring(i);

  return ret;
}

export function escapeHtml(html: string): string {
  return html
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
